import {
  useMutation,
  type UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { fetcher } from "@/shared/core-configs";
import { API } from "@/config";
import type { CreateReposition, Reposition } from "../types/Replenishment";
import { UpdateStockError } from "@/core/common/types";
import { GET_REPOSITION_FETCH_KEY } from "./useGetReposition";

export const UPDATE_REPOSITION_KEY = "updateReposition";

type UpdateRepositionPayload = Partial<CreateReposition>;

const updateReposition = async (
  id: string,
  updatedData: UpdateRepositionPayload,
): Promise<Reposition> => {
  try {
    const response = await fetcher.put(
      `${API.GESLUB}/repositions/${id}`,
      updatedData,
    );
    return response.data;
  } catch (error) {
    console.error(
      "Error en la solicitud de actualización de reposición",
      error,
    );
    throw error;
  }
};

export const useUpdateReposition = (): {
  mutate: UseMutationResult<
    Reposition,
    UpdateStockError,
    { id: string; data: UpdateRepositionPayload }
  >["mutate"];
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: Error | null;
} => {
  const queryClient = useQueryClient();

  const mutation = useMutation<
    Reposition,
    UpdateStockError,
    { id: string; data: UpdateRepositionPayload }
  >({
    mutationFn: ({ id, data }) => updateReposition(id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [GET_REPOSITION_FETCH_KEY],
      });
    },
    onError: (error) => {
      console.error("Error actualizando la reposición:", error);
    },
  });

  return {
    mutate: mutation.mutate,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
  };
};
